﻿import {computed, type ComputedRef, defineComponent, ref, watch} from 'vue';
import {debounce} from "lodash";
import dayjs from "dayjs";
import {AllInvoicesQuery, AthumiService, DocumentType} from "@/api/enterprise-gateway-api.proxy.ts";
import DateUtil from "@/app/shared/helpers/date-util.ts";
import {useInvoicingStore} from "@/app/stores/invoicing-store.ts";
import {type EnumLabelMap, enumToSelectOptions} from "@/app/shared/helpers/enum.ts";
import {getAvailableServices} from "@/app/shared/helpers/services.ts";

export default defineComponent({
    name: 'AegSearchInvoice',
    emits: ['search'],
    props: {
        defaultQuery: {
            type: Object as () => AllInvoicesQuery,
            required: false
        },
        enableCustomerReferenceFilter: {
            type: Boolean,
            required: false,
            default: true
        },
        enableServiceFilter: {
            type: Boolean,
            required: false
        },
        enableDocumentTypeFilter: {
            type: Boolean,
            required: false
        }
    },
    setup: function (props, {emit}) {
        const invoiceStore = useInvoicingStore();
        const dateFrom = ref<string[]>();
        const dateTo = ref<string[]>();
        const documentNumber = ref<string>('');
        const customerReference = ref<string>('');
        const service = ref<AthumiService | undefined>(undefined);
        const selectedDocumentType = ref<DocumentType | undefined>(undefined);

        const serviceOptions = getAvailableServices();

        const documentTypeLabels: EnumLabelMap = {
            Invoice: invoiceStore.getDocumentTypeLabel(DocumentType.Invoice),
            PaymentProof: invoiceStore.getDocumentTypeLabel(DocumentType.PaymentProof),
            PaymentRequest: invoiceStore.getDocumentTypeLabel(DocumentType.PaymentRequest)
        };

        const documentTypeOptions = enumToSelectOptions(DocumentType, documentTypeLabels);
        
        const dateFromMaxDate = computed(() => {
            return DateUtil.formatDate(dateTo.value);
        });

        const dateToMinDate = computed(() => {
            return DateUtil.formatDate(dateFrom.value);
        });

        const watchFields: ComputedRef<AllInvoicesQuery> = computed(() => {
            return AllInvoicesQuery.fromJS({
                fromDate: dateFrom.value && dateFrom.value[0] ? dayjs(dateFrom.value[0]).utc(true).toISOString() : undefined,
                toDate: dateTo.value && dateTo.value[0] ? dayjs(dateTo.value[0]).utc(true).toISOString() : undefined,
                docNr: documentNumber.value,
                customerReference : customerReference.value,
                documentType: selectedDocumentType.value,
                service: service.value
            });
        });

        const debouncedSearch = debounce(async () => {
            emit('search', watchFields.value);
        }, 100);

        const searchFieldChanged = () => {
            debouncedSearch();
        }

        const search = () => {
            emit('search', watchFields.value);
        }

        watch(watchFields, searchFieldChanged, {immediate: false, deep: true});

        watch(() => props.defaultQuery, (newDefaultQuery) => {
            if (newDefaultQuery) {
                if(newDefaultQuery.fromDate) {
                    dateFrom.value = [newDefaultQuery.fromDate];
                }
                if(newDefaultQuery.toDate) {
                    dateTo.value = [newDefaultQuery.toDate];
                }
                service.value = newDefaultQuery.service;
                documentNumber.value = newDefaultQuery.docNr ?? '';
                customerReference.value = newDefaultQuery.customerReference ?? '';
                selectedDocumentType.value = newDefaultQuery.documentType ?? undefined;
            }
        }, {immediate: true, deep: true});
        
        return {
            documentNumber,
            customerReference,
            dateTo,
            dateToMinDate,
            dateFrom,
            dateFromMaxDate,
            serviceOptions,
            documentTypeOptions,
            service,
            selectedDocumentType,
            search,
        }
    }
})
